import type { ReminderDeliveryStatus } from './riskTypes';

type ReminderDisplayStatus =
  | 'none'
  | 'scheduled'
  | ReminderDeliveryStatus
  | 'confirmed'
  | 'rescheduled'
  | 'cancelled'
  | 'no_response';

interface ReminderStatusIconProps {
  appointmentStatus: 'pending' | 'confirmed' | 'rescheduled' | 'cancelled';
  lastReminderOutcome: ReminderDeliveryStatus | undefined;
  firedCount: number;
  outreachFlagged: boolean;
}

const STATUS_CONFIG: Record<
  ReminderDisplayStatus,
  { symbol: string; color: string; title: string; pulse?: boolean }
> = {
  none:        { symbol: '',  color: 'transparent', title: '' },
  scheduled:   { symbol: '→', color: '#94a3b8',   title: 'Reminder scheduled' },
  due:         { symbol: '!', color: '#f59e0b',    title: 'Reminder due', pulse: true },
  delivered:   { symbol: '✓', color: '#22c55e',    title: 'Reminder delivered' },
  failed:      { symbol: '✗', color: '#ef4444',    title: 'Reminder delivery failed' },
  confirmed:   { symbol: '✓✓', color: '#16a34a',   title: 'Patient confirmed' },
  rescheduled: { symbol: '↺', color: '#f59e0b',    title: 'Patient rescheduled' },
  cancelled:   { symbol: '✗', color: '#ef4444',    title: 'Patient cancelled' },
  no_response: { symbol: '⏱', color: '#ef4444',    title: 'No response — outreach flagged' },
};

function deriveStatus(
  appointmentStatus: 'pending' | 'confirmed' | 'rescheduled' | 'cancelled',
  lastReminderOutcome: ReminderDeliveryStatus | undefined,
  firedCount: number,
  outreachFlagged: boolean,
): Exclude<ReminderDisplayStatus, 'none'> {
  if (appointmentStatus === 'confirmed') return 'confirmed';
  if (appointmentStatus === 'rescheduled') return 'rescheduled';
  if (appointmentStatus === 'cancelled') return 'cancelled';
  if (outreachFlagged && lastReminderOutcome === 'delivered') return 'no_response';
  if (lastReminderOutcome === 'delivered') return 'delivered';
  if (lastReminderOutcome === 'due') return 'due';
  if (lastReminderOutcome === 'failed') return 'failed';
  if (firedCount > 0) return 'delivered';
  return 'scheduled';
}

export function ReminderStatusIcon({
  appointmentStatus,
  lastReminderOutcome,
  firedCount,
  outreachFlagged,
}: ReminderStatusIconProps) {
  const status = deriveStatus(appointmentStatus, lastReminderOutcome, firedCount, outreachFlagged);
  const cfg = STATUS_CONFIG[status];

  return (
    <span
      title={cfg.title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 14,
        height: 14,
        borderRadius: 3,
        background: `${cfg.color}22`,
        color: cfg.color,
        fontSize: 8,
        fontWeight: 700,
        flexShrink: 0,
        animation: cfg.pulse ? 'fse-backfill-pulse 1.5s ease-in-out infinite' : 'none',
        userSelect: 'none',
      }}
    >
      {cfg.symbol}
    </span>
  );
}
